import React from 'react';

import { useHistory } from 'react-router-dom';

import { DownloadOutlined, DownOutlined, FlagOutlined, LoadingOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  PlanningStatus,
  RecurrencyRole,
  TenantFeatureFlag,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { UpdateItemLocationInfoType } from '@recurrency/core-api-schema/dist/purchasing/postUpdateItemLocationInfo';
import { ValidationRuleStatus } from '@recurrency/core-api-schema/dist/validationRules/common';
import { ResolutionMethod } from '@recurrency/core-api-schema/dist/validationRules/getRuleDetails';
import { ValidationRuleRunItemLocationDTO } from '@recurrency/core-api-schema/dist/validationRules/getValidationRuleRunsByRuleId';
import { Dropdown, Menu } from 'antd';
import { ZipCelXSheet } from 'zipcelx';

import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { Button } from 'components/Button';
import { Container } from 'components/Container';
import { FlexSpace } from 'components/FlexSpace';
import { ConfirmModal } from 'components/Modal/ConfirmModal';
import { NavTabs } from 'components/NavTabs';
import { PageHeader } from 'components/PageHeader';
import { DetailPageSection, DetailPageSections } from 'components/recipes/detailPage/DetailPageSections';
import { DownloadXSheetColumn, notifyAndDownload, recordsToXSheet } from 'components/recipes/DownloadButton';
import { TextArea } from 'components/TextArea';
import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { captureError } from 'utils/error';
import { formatDate, splitIdNameStr } from 'utils/formatting';
import { arePermissionsEnabled, hasUserPermission, isRecurrencyRole, shouldShowFeatureFlag } from 'utils/roleAndTenant';
import { IdPathParams, routes, usePathParams } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { track, TrackEvent } from 'utils/track';

import { FlagRuleRunsModal } from '../modals/FlagRuleRunsModal';
import { UpsertRuleOrTemplateModal, EditRuleOrTemplateUpdate } from '../modals/UpsertRuleOrTemplateModal';
import { getCategoryDisplayName, handleOnCopy, INPUT_SQL_CTE } from '../utils';
import { RuleDetailRunTable } from './RuleDetailRunTable';

async function getRuleRuns(ruleId: string): Promise<ValidationRuleRunItemLocationDTO[]> {
  const response = await coreApiFetch(schemas.validationRules.getValidationRuleRunsByRuleId, {
    pathParams: { ruleId },
    queryParams: {
      limit: 1_000_000,
    },
  });
  return response.data.items;
}

async function getDownloadData(ruleId: string): Promise<ZipCelXSheet> {
  const items = await getRuleRuns(ruleId);

  const exportColumns: Array<DownloadXSheetColumn<ValidationRuleRunItemLocationDTO>> = [
    {
      title: 'Item ID',
      type: 'string',
      value: (record) => `${record.item_id}`,
    },
    {
      title: 'Item Name',
      type: 'string',
      value: (record) => `${record.item_name}`,
    },
    {
      title: 'Location ID',
      type: 'string',
      value: (record) => record.location,
    },
    {
      title: 'Last Run Time',
      type: 'string',
      value: (record) => record.lastRunTime,
    },
  ];

  return recordsToXSheet(items, exportColumns);
}

export const RuleDetailPage = () => {
  const { activeUser, activeTenant } = useGlobalApp();
  const history = useHistory();
  const { id: ruleId } = usePathParams<IdPathParams>();
  const { data: rule, reload } = useCoreApi(schemas.validationRules.getRuleDetails, {
    pathParams: { ruleId },
  });

  // under permissions the buttons stay visible and disable themselves via permissionsRequired
  const showEditAndDelete = arePermissionsEnabled() || isRecurrencyRole(activeTenant, RecurrencyRole.TenantAdmin);
  const canDownloadRules = hasUserPermission(activeTenant, TenantUserPermission.AccessRuleBuilderRulesDownload);
  const showOverrideRules = shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.FeatureOverrideRules);
  const displaySql = `${INPUT_SQL_CTE}\n${rule?.sql || ''}`;

  const runTableProps = useCoreApiTableProps({
    schema: schemas.validationRules.getValidationRuleRunsByRuleId,
    pathParams: { ruleId },
  });

  function handleDownloadRuns() {
    notifyAndDownload(() => getDownloadData(ruleId));
  }

  const handleDownloadReport = async () => {
    if (!rule) return;

    const downloadNotification = createSubmissionNotification({
      entityName: 'Rule Report',
      submittingMessage: `Generating report for rule [${rule.name}]`,
      expectedWaitSeconds: 120,
    });

    try {
      // Call the new endpoint to get the presigned S3 URL
      // Note: fileType will be ignored by core-api until the schema is updated, but it's safe to include
      const response = await coreApiFetch(schemas.validationRules.createValidationRuleReport, {
        pathParams: {
          tenantId: activeTenant.id,
          ruleId: rule.ruleId,
        },
        bodyParams: {
          fileType: 'csv',
        } as any, // Type assertion needed until core-api-schema package is updated with fileType field
      });

      if (response.data?.downloadUrl) {
        // Create a hidden anchor element to trigger download without opening a new tab
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = `rule-${rule.name}-report.csv`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        downloadNotification.success({
          successMessage: `Report for rule [${rule.name}] is being downloaded.`,
        });
      } else {
        throw new Error('No download URL received from server');
      }
    } catch (err) {
      downloadNotification.error(err);
      captureError(err);
    }
  };

  const handleOnDeploy = async () => {
    const ruleName = rule!.name || '';
    const deploymentNotificaction = createSubmissionNotification({
      entityName: 'Rule',
      submittingMessage: `Deploying rule [${ruleName}]`,
      expectedWaitSeconds: 20,
    });

    try {
      const { data } = await coreApiFetch(schemas.validationRules.postValidationRuleDeployment, {
        pathParams: {
          ruleId: rule!.ruleId,
        },
      });
      if (!data.success) throw new Error(data.error);
      reload();
      // Refresh the run results
      runTableProps.reload();

      deploymentNotificaction.success({
        successMessage: `Rule [${ruleName}] was deployed successfully.`,
      });
    } catch (err) {
      deploymentNotificaction.error(err);
      captureError(err);
    }
  };

  const handleOnDisable = async () => {
    const ruleName = rule!.name || '';
    const deploymentNotificaction = createSubmissionNotification({
      entityName: 'Rule',
      submittingMessage: `Disabling rule [${ruleName}]`,
      expectedWaitSeconds: 10,
    });

    try {
      await coreApiFetch(schemas.validationRules.patchValidationRule, {
        pathParams: {
          ruleId: rule!.ruleId,
        },
        bodyParams: {
          status: ValidationRuleStatus.Draft,
        },
      });
      reload();
      deploymentNotificaction.success({
        successMessage: `Rule [${ruleName}] was disabled successfully.`,
      });
    } catch (err) {
      deploymentNotificaction.error(err);
      captureError(err);
    }
  };

  async function handleSubmitBulkFlagging() {
    if (!rule) return;

    let submitFlaggingNotificaction;

    try {
      submitFlaggingNotificaction = createSubmissionNotification({
        entityName: 'Items',
        submittingMessage: `Flagging all non-flagged items`,
        expectedWaitSeconds: 10,
      });

      const allItems = await getRuleRuns(ruleId);
      // Skip items without planning status or already flagged
      // Items without planning status do not exist in search planning, so we can skip them
      const nonFlaggedItems = allItems.filter(
        (run) => !!run.planning_status && run.planning_status !== PlanningStatus.Flagged,
      );
      if (nonFlaggedItems.length === 0) throw new Error('No items to flag.');

      submitFlaggingNotificaction.inProgress({
        description: () => `Flagging ${nonFlaggedItems.length} items`,
      });

      const flagReason = `Flagged by rule [${rule.name}] at ${formatDate(rule.lastRunTime, true)}`;

      await coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
        pathParams: {
          updateType: UpdateItemLocationInfoType.Flag,
        },
        bodyParams: {
          updates: nonFlaggedItems.map((record) => ({
            itemUid: record.itemId,
            locationId: splitIdNameStr(record.location).foreignId,
            flagged: true,
            flagReason,
          })),
        },
      });

      submitFlaggingNotificaction.success({
        successMessage: `${nonFlaggedItems.length} Items were flagged successfully`,
      });

      track(TrackEvent.ValidationRules_Bulk_Flag, {
        ruleId: rule.ruleId,
        ruleName: rule.name,
        flagged: true,
        flagReason,
        count: nonFlaggedItems.length,
        recordUrl: window.location.origin + routes.ruleBuilder.ruleDetails(rule.ruleId),
      });
    } catch (err) {
      if (err instanceof Error && err.message === 'No items to flag.') {
        submitFlaggingNotificaction?.error(err);
        return;
      }
      submitFlaggingNotificaction?.error(err);
    }
  }

  // Currently built for internal use only
  async function handleSubmitEditRule(updates: EditRuleOrTemplateUpdate) {
    if (!updates || !rule) return;

    const ruleName = updates.name || '';
    const deploymentNotificaction = createSubmissionNotification({
      entityName: 'Rule',
      submittingMessage: `Updating rule [${ruleName}]`,
      expectedWaitSeconds: 10,
    });

    try {
      await coreApiFetch(schemas.validationRules.patchValidationRule, {
        pathParams: {
          ruleId: rule.ruleId,
        },
        bodyParams: {
          name: updates.name,
          description: updates.description,
          sql: updates.sql,
          isDailyWorkflow: updates.isDailyWorkflow,
          isOverridesEnabled: updates.isOverridesEnabled,
          category: updates.ruleCategory,
        },
      });
      deploymentNotificaction.success({
        successMessage: `Rule [${ruleName}] was updated successfully.`,
      });
      reload();
    } catch (err) {
      deploymentNotificaction.error(err);
      captureError(err);
    }
  }

  async function handleOpenFlagModal() {
    await showAsyncModal(FlagRuleRunsModal, {
      rule,
      onSubmit: handleSubmitBulkFlagging,
    });
  }

  async function handleOpenEditModal() {
    if (rule) {
      await showAsyncModal(UpsertRuleOrTemplateModal, {
        rule: {
          name: rule.name,
          description: rule.description,
          sql: rule.sql,
          isDailyWorkflow: rule.isDailyWorkflow,
          isOverridesEnabled: rule.isOverridesEnabled,
          ruleCategory: rule.category,
        },
        onSubmit: handleSubmitEditRule,
        hideSql: !!rule.templateId || !activeUser.isRecurrencyInternalUser,
      });
    }
  }

  async function handleDelete() {
    if (rule) {
      const confirmed = await showAsyncModal(ConfirmModal, {
        title: `Delete Rule: ${rule.name}`,
        content: (
          <FlexSpace direction="column" gap={16}>
            <Typography>This will delete the rule: {rule.name}.</Typography>
            <Typography>Are you sure you want to continue?</Typography>
          </FlexSpace>
        ),
        okText: 'Delete',
        cancelText: 'Cancel',
      });
      if (confirmed) {
        const deleteNotification = createSubmissionNotification({
          entityName: 'Rule',
          submittingMessage: `Deleting rule [${rule.name}]`,
          expectedWaitSeconds: 5,
        });
        try {
          await coreApiFetch(schemas.validationRules.patchValidationRule, {
            pathParams: {
              ruleId: rule.ruleId,
            },
            bodyParams: {
              isActive: false,
              status: ValidationRuleStatus.Draft,
            },
          });
          history.push(routes.ruleBuilder.ruleManagement());
          deleteNotification.success({
            successMessage: `Rule [${rule.name}] was deleted successfully.`,
          });
        } catch (err) {
          deleteNotification.error(err);
          captureError(err);
        }
      }
    }
  }

  const headerButtons = [
    <Button
      key="deploy"
      permissionsRequired={[TenantUserPermission.ActionCreateRules]}
      onClick={handleOnDeploy}
      disabled={!rule || rule?.status !== ValidationRuleStatus.Draft}
      style={{
        display: rule?.status === ValidationRuleStatus.Draft ? 'inline-block' : 'none',
      }}
    >
      Deploy
    </Button>,
    <Button
      key="disable"
      permissionsRequired={[TenantUserPermission.ActionCreateRules]}
      onClick={handleOnDisable}
      disabled={!rule || rule?.status !== ValidationRuleStatus.Deployed}
      style={{
        display: rule?.status === ValidationRuleStatus.Deployed ? 'inline-block' : 'none',
      }}
    >
      Disable
    </Button>,
    showEditAndDelete ? (
      <Button
        key="edit"
        permissionsRequired={[TenantUserPermission.ActionCreateRules]}
        onClick={handleOpenEditModal}
        disabled={!rule}
      >
        Edit
      </Button>
    ) : null,
    showEditAndDelete ? (
      <Button
        key="delete"
        permissionsRequired={[TenantUserPermission.ActionCreateRules]}
        onClick={handleDelete}
        disabled={!rule}
      >
        Delete
      </Button>
    ) : null,
    <Dropdown
      key="bulk-actions"
      overlay={
        <Menu>
          {rule?.resolutionMethods?.includes(ResolutionMethod.Flag) ? (
            <Menu.Item disabled={!rule} key="flag" icon={<FlagOutlined />} onClick={handleOpenFlagModal}>
              Flag all items
            </Menu.Item>
          ) : null}
          <Menu.Item
            key="download"
            icon={<DownloadOutlined />}
            disabled={!canDownloadRules}
            onClick={handleDownloadRuns}
          >
            Download all runs
          </Menu.Item>
          <Menu.Item
            key="download-report"
            icon={<DownloadOutlined />}
            disabled={!canDownloadRules}
            onClick={handleDownloadReport}
          >
            Download CSV report
          </Menu.Item>
        </Menu>
      }
    >
      <Button type="primary" title="Select table records to enable bulk actions">
        Bulk Actions <DownOutlined />
      </Button>
    </Dropdown>,
  ];

  const headerSections: DetailPageSection[] = [
    {
      title: 'Rule Configuration',
      rows: [
        [
          {
            label: 'Rule Name',
            value: rule?.name,
          },
          {
            label: 'Rule Description',
            value: rule?.description,
          },
          showOverrideRules
            ? {
                label: 'Is Override Rule',
                value: rule?.isOverridesEnabled ? 'Yes' : 'No',
              }
            : null,
        ].filter(truthy),
        [
          {
            label: 'Rule Type',
            value: rule?.status,
          },
          {
            label: 'Last Run Time',
            value: formatDate(rule?.lastRunTime, true, true),
          },
          {
            label: 'Is Daily Workflow',
            value: rule?.isDailyWorkflow ? 'Yes' : 'No',
          },
        ].filter(truthy),
        [
          {
            label: 'Template Source',
            value: rule?.templateId ? `${rule.templateName}` : '-',
          },
          {
            label: 'Created At',
            value: formatDate(rule?.createdAt, true, true),
          },
          {
            label: 'Updated At',
            value: formatDate(rule?.updatedAt, true, true),
          },
        ],
        [
          {
            label: 'Category',
            value: rule?.category ? getCategoryDisplayName(rule.category) : '-',
          },
        ],
      ],
    },
    {
      title: 'Rule SQL',
      isCollapsedByDefault: true,
      rows: [
        [
          {
            label: '',
            value: (
              <>
                <TextArea disabled autoSize value={displaySql} />
                <Button onClick={() => handleOnCopy(rule?.sql || '')} type="dashed" style={{ marginTop: 10 }}>
                  Copy Runnable SQL
                </Button>
              </>
            ),
            colspan: 2,
          },
        ],
      ],
    },
  ];

  return (
    <Container>
      <PageHeader
        title={`Rule ${rule?.name}`}
        copyable
        entity={{
          kind: 'Rule Details',
        }}
        headerActions={headerButtons}
      />
      <DetailPageSections sections={headerSections} />
      <div
        className={css`
          .ant-tabs {
            // Unsetting overflow so that sticky headers and side panel css will work
            overflow: unset;
          }
        `}
      >
        <NavTabs
          tabs={[
            {
              header: 'Rule Runs',
              content: rule ? <RuleDetailRunTable tableProps={runTableProps} /> : <LoadingOutlined />,
            },
          ]}
        />
      </div>
    </Container>
  );
};
